import { Injectable } from '@angular/core';
import { Platform, PopoverController } from '@ionic/angular';
import { Content } from 'src/app/models/content';
import { ContentContainer } from 'src/app/models/container';
import { ShareLinkMenuPage } from 'src/app/pages/_core/share-link-menu/share-link-menu.page';
import { ErrorHandlerService } from '../_core/error-handler/error-handler.service';
import { NotificationsService } from '../_core/notifications/notifications.service';
import { environment } from 'src/environments/environment';

export interface ShareLinkOptions {
  url: string;
  title: string;
  copiedToast?: string;
  context?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ShareLinkService {

  constructor(
    private platform: Platform,
    private popCtrl: PopoverController,
    private notifications: NotificationsService,
    private errorHandler: ErrorHandlerService
  ) { }

  getContentShareUrl(
    content: Content,
    classContainer?: ContentContainer,
    categoryContainer?: ContentContainer,
    appContainer?: ContentContainer
  ): string {
    const appBaseUrl = this.getBaseUrl();

    if (content?.type === 'GOLIVE') {
      return `${appBaseUrl}/tabs/golive/${content.id}`;
    }

    const classId = classContainer?.id || content?.ClassContainer?.id || content?.classContainerId;
    const categoryId = categoryContainer?.id || content?.SubCategoryContainer?.id || content?.subCategoryContainerId;
    const appId = appContainer?.id || content?.AppContainer?.id || content?.appContainerId;

    if (classId && categoryId && appId && content?.id) {
      return `${appBaseUrl}/tabs/explore/${classId}/${categoryId}/${appId}/${content.id}`;
    }

    return `${appBaseUrl}/tabs/content/${content?.id}`;
  }

  getContainerShareUrl(
    container: ContentContainer,
    classContainer?: ContentContainer,
    categoryContainer?: ContentContainer
  ): string {
    const appBaseUrl = this.getBaseUrl();

    if (container?.type === 'GOLIVE') {
      return `${appBaseUrl}/tabs/golive/${container.id}`;
    }

    const classId = classContainer?.id || environment.homeClassId;
    const categoryId = categoryContainer?.id || container?.parentId;
    const appId = container?.id;

    if (classId && categoryId && appId) {
      return `${appBaseUrl}/tabs/explore/${classId}/${categoryId}/${appId}`;
    }

    return `${appBaseUrl}/tabs/content/${container?.id}`;
  }

  async openShareMenu(ev: Event, options: ShareLinkOptions): Promise<void> {
    const pop = await this.popCtrl.create({
      component: ShareLinkMenuPage,
      event: ev,
      componentProps: {
        url: options.url,
        title: options.title,
        copiedToast: options.copiedToast
      }
    });
    await pop.present();
  }

  async copyToClipboard(
    url: string,
    copiedToast = 'Link copied.',
    context = 'share-link.copyToClipboard'
  ): Promise<void> {
    try {
      if ((navigator as any)?.clipboard?.writeText) {
        await (navigator as any).clipboard.writeText(url);
        this.notifications.showToast(copiedToast);
      } else {
        window.open(url, '_blank');
      }
    } catch (err) {
      this.errorHandler.handleError(err, context);
    }
  }

  async shareNative(
    url: string,
    title: string,
    context = 'share-link.shareNative'
  ): Promise<void> {
    try {
      if ((navigator as any)?.share) {
        await (navigator as any).share({
          title,
          text: title,
          url
        });
      } else {
        await this.copyToClipboard(url, 'Link copied.', context);
      }
    } catch (err) {
      if ((err as Error)?.name === 'AbortError') {
        return;
      }
      this.errorHandler.handleError(err, context);
    }
  }

  shareViaEmail(url: string, title: string): void {
    const subject = encodeURIComponent(`Check out: ${title}`);
    const body = encodeURIComponent(this.getShareMessageBody(url, title));
    location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  shareViaOutlook(url: string, title: string): void {
    const subject = encodeURIComponent(`Check out: ${title}`);
    const body = encodeURIComponent(this.getShareMessageBody(url, title));
    const outlookUrl = `https://outlook.office.com/mail/deeplink/compose?subject=${subject}&body=${body}`;
    this.openShareWindow(outlookUrl);
  }

  shareViaTeams(url: string, title: string): void {
    const msgText = this.truncateMsgText(title);
    const teamsUrl =
      `https://teams.microsoft.com/share?href=${encodeURIComponent(url)}` +
      `&msgText=${encodeURIComponent(msgText)}&preview=true`;
    this.openShareWindow(teamsUrl);
  }

  shareViaText(url: string, title: string): void {
    const body = encodeURIComponent(`${title} - ${url}`);
    if (this.platform.is('ios')) {
      location.href = `sms:&body=${body}`;
    } else {
      location.href = `sms:?body=${body}`;
    }
  }

  private getBaseUrl(): string {
    return (environment?.webUrl || window.location.origin).replace(/\/$/, '');
  }

  private getShareMessageBody(url: string, title: string): string {
    return `Hi,\n\nI wanted to share this WorkTips content with you:\n${title}\n\n${url}\n`;
  }

  private truncateMsgText(text: string, maxLength = 200): string {
    if (!text || text.length <= maxLength) {
      return text || '';
    }
    return `${text.substring(0, maxLength - 3)}...`;
  }

  private openShareWindow(url: string): void {
    if (this.platform.is('mobile') && !this.platform.is('mobileweb')) {
      window.open(url, '_system');
    } else {
      window.open(url, '_blank', 'width=600,height=600,scrollbars=no,resizable=no');
    }
  }

}
